import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import type { Notification } from "../types/notification.types.ts"
import { notificationsSliceName } from "./notification.constants.ts"
import type { NotificationState } from "./notification.types.ts"
import {
  fetchNotificationSettings,
  updateNotificationSettings
} from "./notifications.thunks.ts"

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  hasNewNotification: false,
  hasMore: false,
  next: null,
  notificationSettings: null,
  fetchingSettings: false,
  settingsError: false,
  isUpdatingSettings: false
}

const notificationSlice = createSlice({
  name: notificationsSliceName,
  initialState,
  reducers: {
    setInitialNotifications: (
      state,
      action: PayloadAction<{
        notifications: Notification[]
        unreadCount: number
        hasMore?: boolean
        next?: number | null
      }>
    ) => {
      state.notifications = action.payload.notifications
      state.unreadCount = action.payload.unreadCount
      state.hasMore = action.payload.hasMore || false
      state.next = action.payload.next || null
      state.hasNewNotification = false
    },

    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload)
      state.unreadCount += 1
    },

    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload
    },

    markAsRead: (state, action: PayloadAction<number | null>) => {
      if (action.payload === null) {
        state.notifications = state.notifications.map((n) => ({
          ...n,
          unread: false
        }))
        state.unreadCount = 0
      } else {
        state.notifications = state.notifications.map((n) =>
          n.id === action.payload ? { ...n, unread: false } : n
        )
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    },

    deleteNotifications: (state, action: PayloadAction<number | null>) => {
      if (action.payload === null) {
        state.notifications = []
        state.unreadCount = 0
        state.hasNewNotification = false
      } else {
        state.notifications = state.notifications.filter((n) => {
          return n.id !== action.payload
        })
      }
    },

    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
    },

    setHasNewNotification: (state, action: PayloadAction<boolean>) => {
      state.hasNewNotification = action.payload
      if (action.payload) {
        state.unreadCount += 1
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, () => initialState)
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.fetchingSettings = true
        state.settingsError = false
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.fetchingSettings = false
        state.settingsError = false
        state.notificationSettings = action.payload
      })
      .addCase(fetchNotificationSettings.rejected, (state) => {
        state.fetchingSettings = false
        state.settingsError = true
      })
      .addCase(updateNotificationSettings.pending, (state) => {
        state.isUpdatingSettings = true
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.isUpdatingSettings = false
        if (!state.notificationSettings) return

        const updatesMap = new Map(
          action.payload.map((p) => [p.notification_type, p])
        )

        state.notificationSettings = Object.fromEntries(
          Object.entries(state.notificationSettings).map(([key, items]) => [
            key,
            items.map((item) => ({
              ...item,
              ...updatesMap.get(item.notification_type)
            }))
          ])
        )
      })
      .addCase(updateNotificationSettings.rejected, (state) => {
        state.isUpdatingSettings = false
      })
  }
})

export const {
  setInitialNotifications,
  addNotification,
  markAsRead,
  setConnected,
  setHasNewNotification,
  deleteNotifications,
  setUnreadCount
} = notificationSlice.actions

export default notificationSlice
